<template>
  <div class="price-bid-description">

    <VFormItem
      :label="$core.currentPartner.isSeller ? 'Ваша цена' : 'Цена поставщика'"
      layout="vertical"
    >
      <VInputNumberRusV2
        v-model="orderOption.priceSeller"
        :type="Price"
        required
        layout="vertical"
        class="price-control"
        :validate-method="validateLocalPrice"
        :disabled="disabled"
      />

      <VSelect
        v-model="localEntityOfferCurrency"
        required
        :options="Core.currencies.currencies"
        :clearable="false"
        option-label="symbol"
        option-identity="code"
        class="currency-control"
        :disabled="disabled"
      />

      <template v-if="tenderBid" #helper-text>
        <div v-if="showBestBidHint" class="helper-text-price-clue">
          Чтобы стать лидером нужно
          <!-- Конвертации нет: лучшая цена считается сразу в валюте офера. -->
          <VLink @click="tenderBid.priceSeller = tenderBid.newBestPrice">
            установить цену
            {{ tenderBid.newBestPrice.format(tenderBid.offerCurrencyCode) }}
          </VLink>
        </div>

        <VHelperText
          v-if="showPriceStart && tenderBid"
          theme="blue-flat"
        >
          <dl>
            <dt>Стартовая цена</dt>
            <dd>{{
                tenderBid.tender.priceStartFull.format(tenderBid.tender.priceStartCurrencyCode)
              }}
            </dd>
          </dl>
        </VHelperText>
      </template>
    </VFormItem>

    <dl>
      <dt>Конечная цена для покупателя</dt>
      <dd>{{ finalPrice?.format(orderOption.offerCurrencyCode) }}</dd>

      <template v-if="$core.displayedCurrencyCode !== orderOption.offerCurrencyCode">
        <dt class="secondary grey">В валюте сайта</dt>
        <dd class="secondary grey">{{
            finalPrice?.formatInDisplayCurrency(orderOption.offerCurrencyCode, orderOption.getDateForCurrencyRate())
          }}
        </dd>
      </template>


    </dl>

    <dl
      class="cursor-pointer"
      @mouseenter="$core.dialogs.PriceModPopup.show({ orderOption, target: $event.target })"
      @mouseleave="$core.dialogs.PriceModPopup.hide()"
    >
      <dt>
        Наценка
        <VLink material="info"/>
      </dt>
      <dd>{{ orderOption.priceModTotal }}</dd>
    </dl>

    <dl
      v-if="orderOption.deliveryType.id === 'byETP'"
    >
      <dt>Стоимость провоза</dt>
      <dd>{{
          orderOption.deliveryPricePerUnit?.format(orderOption.offerCurrencyCode, orderOption.unit)
        }}
      </dd>
    </dl>
  </div>
</template>

<style lang="scss" src="./PriceDescription.scss"/>

<script lang="ts">

import Core from "@/core/Core";
import Utils from '@/core/utils/Utils'
import TenderBid from '@/domains/TenderBids/TenderBid';
import DemandOption from "@/domains/DemandsV2/DemandOption";
import Price from "@/core/types/Price";
import {UnwrapNestedRefs} from "vue";

export default defineComponent({
  name: 'PriceDescription',

  props: {
    /** orderOption это опция потребности или ставка тендера */
    orderOption: {
      type: Object as PropType<TenderBid | DemandOption | UnwrapNestedRefs<TenderBid | DemandOption>>,
      required: true
    },
    entityType: {
      type: String,
      validator: value => ['bid', 'option'].includes(value),
      required: true,
    },
    disabled: {type: Boolean, default: false},
    /** только для тендера */
    bestBid: {
      type: Object as PropType<TenderBid | UnwrapNestedRefs<TenderBid>>,
      default: null
    },
    showPriceStart: {type: Boolean, default: false},
  },

  setup() {
    return {
      Utils,
      Core,
      /** Класс нужен шаблону для :type у VInputNumberRusV2 */
      Price,
    };
  },

  computed: {
    isTenderBid() {
      return this.entityType === 'bid';
    },

    tenderBid() {
      if (this.orderOption instanceof TenderBid || this.isTenderBid) return this.orderOption as TenderBid;
      return null;
    },

    demandOption() {
      if (this.orderOption instanceof DemandOption || !this.isTenderBid) return this.orderOption as DemandOption;
      return null;
    },

    showBestBidHint() {
      // Если тендер окончен, то ничего не показываем
      if (this.orderOption.tender.isFinished === true) return false;
      // Подсказка доступна только селлеру
      if (this.$core.currentPartner.isSeller === false) return false;
      // если поставщик и так обладатель лучшей ставки, то прячем подсказку
      if (this.bestBid?.isMine === true) return false;
      // Чтобы показать, должна быть цена
      if (this.tenderBid.newBestPrice.isEmpty() === true) return false;

      return true
    },

    localEntityOfferCurrency: {
      set({code}) {
        this.orderOption.offerCurrencyCode = code;
      },
      get() {
        return Core.currencies.currencies.get(this.orderOption.offerCurrencyCode) ?? null;
      },
    },

    finalPrice(): Price | null {
      if (this.tenderBid) return this.tenderBid.priceFullBuyerWithDeferredPayment;
      if (this.demandOption) return this.demandOption.priceFullSeller;

      return null;
    },
  },

  methods: {
    async validateLocalPrice() {
      if (this.tenderBid === null) return;

      /** Лучшей ставки может и не быть **/
      if (this.bestBid === null) return;
      /** Если лучшая ставка моя, и я хочу обновить в ставке что-нибудь, то валидация не нужна */
      if (this.bestBid.isMine === true) return null;

      if (await this.tenderBid.isPriceFullBuyerEqual(this.bestBid)) {
        return 'Нельзя поставить цену равную лучшей';
      }

      return null;
    },
  },
});
</script>
